import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';
import {
  removeUserAccountAccess,
  saveUserAccountChanges,
  editUserData,
  fetchUserWithAccounts,
  createUser,
  checkEmailExists,
  validateUserData,
} from '../api';

const initialState = {
  // User data being managed
  managedUser: null,
  managedUserStatus: REQUEST_STATUS.idle,
  managedUserError: null,

  // Original user data for comparing changes
  originalManagedUser: null,

  // User's accounts list
  userAccounts: [],
  userAccountsStatus: REQUEST_STATUS.idle,
  userAccountsError: null,

  // Currently selected account
  selectedAccount: null,

  // Preselected account from global users management
  preselectedAccountFromGlobal: null,

  // Saving status for updates
  saveStatus: REQUEST_STATUS.idle,
  saveError: null,
  saveValidationErrors: null,

  // Remove account access status
  removeAccountStatus: REQUEST_STATUS.idle,
  removeAccountError: null,

  // Edit user data status
  editUserStatus: REQUEST_STATUS.idle,
  editUserError: null,

  // Create user status
  createUserStatus: REQUEST_STATUS.idle,
  createUserError: null,

  // Validate user data status
  validateUserStatus: REQUEST_STATUS.idle,
  validateUserError: null,
  validateUserValidationErrors: null,

  // Fetch user with accounts status
  fetchUserStatus: REQUEST_STATUS.idle,
  fetchUserError: null,

  // Create user flow state
  createUserFlow: {
    currentStep: 1,
    emailCheckStatus: REQUEST_STATUS.idle, // idle | loading | succeeded | failed
    checkedEmail: '',
    existingUser: null,
    emailError: null,
    preFilledUserData: null, // For storing user data when jumping to step 3 for existing users
  },
};

export const userAccountManagementSlice = createSlice({
  name: 'userAccountManagement',
  initialState,
  reducers: {
    // Set managed user data
    setManagedUser: (state, action) => {
      state.managedUser = action.payload;
      state.originalManagedUser = action.payload; // Also save as original for comparison
      state.managedUserStatus = REQUEST_STATUS.succeeded;
    },

    // Update managed user data locally
    updateManagedUserLocal: (state, action) => {
      state.managedUser = {
        ...state.managedUser,
        ...action.payload,
      };
    },

    // Set user accounts
    setUserAccounts: (state, action) => {
      state.userAccounts = action.payload;
      state.userAccountsStatus = REQUEST_STATUS.succeeded;
      // Auto-select first account if none selected
      if (!state.selectedAccount && action.payload.length > 0) {
        state.selectedAccount = action.payload[0].accountUUID;
      }
    },

    // Set selected account
    setSelectedAccount: (state, action) => {
      state.selectedAccount = action.payload;
    },

    // Set preselected account from global users management
    setPreselectedAccountFromGlobal: (state, action) => {
      state.preselectedAccountFromGlobal = action.payload;
    },

    // Add account to the list
    addUserAccount: (state, action) => {
      const account = action.payload;
      // Generate unique ID for the new account

      const newAccount = {
        ...account, // Include any additional fields
        isNew: true, // Mark as new account
        addedAt: Date.now(), // Timestamp for auto-removal
      };

      // Add to the beginning of the array instead of end
      state.userAccounts.unshift(newAccount);
    },

    // Remove account from the list
    removeUserAccount: (state, action) => {
      const accountId = action.payload;
      state.userAccounts = state.userAccounts.filter(account => account.id !== accountId);
    },

    // Remove isNew flag from account
    markAccountAsNotNew: (state, action) => {
      const accountId = action.payload;
      const accountIndex = state.userAccounts.findIndex(account => account.id === accountId);
      if (accountIndex !== -1) {
        state.userAccounts[accountIndex].isNew = false;
        delete state.userAccounts[accountIndex].addedAt;
      }
    },

    // Remove isNew flag from all accounts older than specified time
    cleanupNewAccounts: (state, action) => {
      const cutoffTime = action.payload || Date.now() - (30 * 1000); // Default 30 seconds
      state.userAccounts.forEach(account => {
        if (account.isNew && account.addedAt && account.addedAt < cutoffTime) {
          account.isNew = false;
          delete account.addedAt;
        }
      });
    },

    // Update account permissions and details
    updateUserAccount: (state, action) => {
      const { accountId, updates } = action.payload;
      const accountIndex = state.userAccounts.findIndex(account => account.id === accountId);

      if (accountIndex !== -1) {
        // Update account with new data
        if (updates.userPermissions) {
          state.userAccounts[accountIndex] = {
            ...state.userAccounts[accountIndex],
            role: updates.role,
            userPermissions: updates.userPermissions,
            providerTitle: updates.providerTitle || '',
            providerNPINumber: updates.providerNPINumber || '',
          };
        } else {
          state.userAccounts[accountIndex] = {
            ...state.userAccounts[accountIndex],
            ...updates,
          };
        }
      }
    },

    // Set loading states
    setManagedUserLoading: (state) => {
      state.managedUserStatus = REQUEST_STATUS.loading;
    },

    setUserAccountsLoading: (state) => {
      state.userAccountsStatus = REQUEST_STATUS.loading;
    },

    setSaveLoading: (state) => {
      state.saveStatus = REQUEST_STATUS.loading;
    },

    setSaveSuccess: (state) => {
      state.saveStatus = REQUEST_STATUS.succeeded;
    },

    setSaveError: (state, action) => {
      state.saveStatus = REQUEST_STATUS.failed;
      state.saveError = action.payload;
    },

    // Reset the slice
    resetUserAccountManagement: () => initialState,

    // Reset edit status
    resetEditUserStatus: (state) => {
      state.editUserStatus = REQUEST_STATUS.idle;
      state.editUserError = null;
    },

    // Create user flow actions
    setCreateUserStep: (state, action) => {
      state.createUserFlow.currentStep = action.payload;
    },

    setEmailCheckStatus: (state, action) => {
      state.createUserFlow.emailCheckStatus = action.payload;
    },

    setCheckedEmail: (state, action) => {
      state.createUserFlow.checkedEmail = action.payload;
    },

    setExistingUser: (state, action) => {
      state.createUserFlow.existingUser = action.payload;
    },

    setEmailError: (state, action) => {
      state.createUserFlow.emailError = action.payload;
    },

    setPreFilledUserData: (state, action) => {
      state.createUserFlow.preFilledUserData = action.payload;
    },

    resetCreateUserFlow: (state) => {
      state.createUserFlow = {
        currentStep: 1,
        emailCheckStatus: REQUEST_STATUS.idle,
        checkedEmail: '',
        existingUser: null,
        emailError: null,
        preFilledUserData: null,
      };
      // Also reset create user status
      state.createUserStatus = REQUEST_STATUS.idle;
      state.createUserError = null;
    },

    resetCreateUserStatus: (state) => {
      state.createUserStatus = REQUEST_STATUS.idle;
      state.createUserError = null;
    },

    resetValidateUserStatus: (state) => {
      state.validateUserStatus = REQUEST_STATUS.idle;
      state.validateUserError = null;
      state.validateUserValidationErrors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle remove user account access
      .addCase(removeUserAccountAccess.pending, (state) => {
        state.removeAccountStatus = REQUEST_STATUS.loading;
        state.removeAccountError = null;
      })
      .addCase(removeUserAccountAccess.fulfilled, (state, action) => {
        state.removeAccountStatus = REQUEST_STATUS.succeeded;

        const { accountId, accountName } = action.payload;

        // Find the account being removed to get its accountUUID for comparison
        // accountId might be either account.id or account.accountUUID depending on data structure
        const accountToRemove = state.userAccounts.find(account =>
          account.id === accountId || account.accountUUID === accountId,
        );
        const accountUUIDToRemove = accountToRemove?.accountUUID;

        // Remove the account from the userAccounts array
        state.userAccounts = state.userAccounts.filter(account =>
          account.id !== accountId && account.accountUUID !== accountId,
        );

        // If we're removing the currently selected account, switch to the first remaining one
        if (accountUUIDToRemove === state.selectedAccount) {
          if (state.userAccounts.length > 0) {
            state.selectedAccount = state.userAccounts[0].accountUUID;
          } else {
            state.selectedAccount = null;
          }
        }
      })
      .addCase(removeUserAccountAccess.rejected, (state, action) => {
        state.removeAccountStatus = REQUEST_STATUS.failed;
        state.removeAccountError = action.payload?.error?.message || 'Failed to remove account access';
      })
      // Handle save user account changes
      .addCase(saveUserAccountChanges.pending, (state) => {
        state.saveStatus = REQUEST_STATUS.loading;
        state.saveError = null;
        state.saveValidationErrors = null;
      })
      .addCase(saveUserAccountChanges.fulfilled, (state, action) => {
        state.saveStatus = REQUEST_STATUS.succeeded;
        const { updatedUser, formValues, selectedAccount, noChanges } = action.payload;

        // Don't update state if there were no changes
        if (noChanges) {
          return;
        }

        // Update managed user data with API response (if available) or formValues
        if (updatedUser) {
          state.managedUser = { ...state.managedUser, ...updatedUser };
          state.originalManagedUser = { ...state.managedUser, ...updatedUser };
        } else if (formValues) {
          // Fallback to formValues if API doesn't return updated data
          state.managedUser = { ...state.managedUser, ...formValues };
          state.originalManagedUser = { ...state.managedUser, ...formValues };
        }

        // Also update account data locally for UI consistency
        if (formValues && selectedAccount) {
          const accountIndex = state.userAccounts.findIndex(account =>
            account.accountUUID === selectedAccount,
          );
          if (accountIndex !== -1) {
            // Update account with new permissions
            if (formValues.userPermissions) {
              state.userAccounts[accountIndex] = {
                ...state.userAccounts[accountIndex],
                role: formValues.role,
                userPermissions: formValues.userPermissions,
                providerTitle: formValues.providerTitle || '',
                providerNPINumber: formValues.providerNPINumber || '',
                allowedAccounts: formValues.allowedAccounts || [],
              };
            }
          }
        }
      })
      .addCase(saveUserAccountChanges.rejected, (state, action) => {
        state.saveStatus = REQUEST_STATUS.failed;
        state.saveError = action.payload?.error || action.payload?.message || 'Failed to save changes';
        state.saveValidationErrors = action.payload?.validationErrors || null;
      })
      // Handle edit user data
      .addCase(editUserData.pending, (state) => {
        state.editUserStatus = REQUEST_STATUS.loading;
        state.editUserError = null;
      })
      .addCase(editUserData.fulfilled, (state, action) => {
        state.editUserStatus = REQUEST_STATUS.succeeded;
        const { updatedUser, formValues, noChanges } = action.payload;

        // Don't update state if there were no changes
        if (noChanges) {
          return;
        }

        // Update managed user data with API response (if available) or formValues
        if (updatedUser) {
          state.managedUser = { ...state.managedUser, ...updatedUser };
          state.originalManagedUser = { ...state.managedUser, ...updatedUser };
        } else if (formValues) {
          // Fallback to formValues if API doesn't return updated data
          state.managedUser = { ...state.managedUser, ...formValues };
          state.originalManagedUser = { ...state.managedUser, ...formValues };
        }
      })
      .addCase(editUserData.rejected, (state, action) => {
        state.editUserStatus = REQUEST_STATUS.failed;
        state.editUserError = action.payload?.error?.message || 'Failed to edit user';
      })
      // Handle create user
      .addCase(createUser.pending, (state) => {
        state.createUserStatus = REQUEST_STATUS.loading;
        state.createUserError = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createUserStatus = REQUEST_STATUS.succeeded;
        const { createdUser } = action.payload;
        // Store the created user data if needed
        // No need to update managedUser here as we'll navigate away
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createUserStatus = REQUEST_STATUS.failed;
        state.createUserError = action.payload?.error?.message || 'Failed to create user';
      })
      // Handle fetch user with accounts
      .addCase(fetchUserWithAccounts.pending, (state) => {
        state.fetchUserStatus = REQUEST_STATUS.loading;
        state.fetchUserError = null;
        state.managedUserStatus = REQUEST_STATUS.loading;
        state.userAccountsStatus = REQUEST_STATUS.loading;
      })
      .addCase(fetchUserWithAccounts.fulfilled, (state, action) => {
        state.fetchUserStatus = REQUEST_STATUS.succeeded;
        const { user, userAccounts } = action.payload;

        // Set managed user data
        state.managedUser = user;
        state.originalManagedUser = user;
        state.managedUserStatus = REQUEST_STATUS.succeeded;

        // Note: We'll need currentAccountUuid and titles from the component
        // For now, set raw data and transform in the component dispatch
        state.userAccounts = userAccounts || [];
        state.userAccountsStatus = REQUEST_STATUS.succeeded;
        if (state.userAccounts.length > 0) {
          if (state.preselectedAccountFromGlobal && userAccounts.find(acc => acc.accountUUID === state.preselectedAccountFromGlobal)) {
            state.selectedAccount = state.preselectedAccountFromGlobal;
            // Clear the preselected account after use
            state.preselectedAccountFromGlobal = null;
          } else {
            state.selectedAccount = userAccounts[0].accountUUID;
          }
        }
      })
      .addCase(fetchUserWithAccounts.rejected, (state, action) => {
        state.fetchUserStatus = REQUEST_STATUS.failed;
        state.fetchUserError = action.payload?.error?.message || 'Failed to fetch user data';
        state.managedUserStatus = REQUEST_STATUS.failed;
        state.userAccountsStatus = REQUEST_STATUS.failed;
        state.managedUserError = action.payload?.error?.message;
        state.userAccountsError = action.payload?.error?.message;
      })
      // Handle check email exists
      .addCase(checkEmailExists.pending, (state, action) => {
        state.createUserFlow.emailCheckStatus = REQUEST_STATUS.loading;
        state.createUserFlow.checkedEmail = action.meta.arg.email;
        state.createUserFlow.existingUser = null;
        state.createUserFlow.emailError = null;
      })
      .addCase(checkEmailExists.fulfilled, (state, action) => {
        state.createUserFlow.emailCheckStatus = REQUEST_STATUS.succeeded;
        state.createUserFlow.checkedEmail = action.payload.email;

        if (action.payload.exists) {
          state.createUserFlow.existingUser = action.payload.user;
        } else {
          state.createUserFlow.existingUser = null;
        }
      })
      .addCase(checkEmailExists.rejected, (state, action) => {

        state.createUserFlow.emailCheckStatus = REQUEST_STATUS.failed;
        state.createUserFlow.existingUser = null;
        state.createUserFlow.emailError = action.payload?.error || 'Error checking email';

        if (action.payload?.email) {
          state.createUserFlow.checkedEmail = action.payload.email;
        }
      })
      // Handle validate user data
      .addCase(validateUserData.pending, (state) => {
        state.validateUserStatus = REQUEST_STATUS.loading;
        state.validateUserError = null;
        state.validateUserValidationErrors = null;
      })
      .addCase(validateUserData.fulfilled, (state, action) => {
        state.validateUserStatus = REQUEST_STATUS.succeeded;
      })
      .addCase(validateUserData.rejected, (state, action) => {
        state.validateUserStatus = REQUEST_STATUS.failed;
        state.validateUserError = action.payload?.error || 'Validation failed';
        state.validateUserValidationErrors = action.payload?.validationErrors || null;
      });
  },
});

export const {
  setManagedUser,
  updateManagedUserLocal,
  setUserAccounts,
  setSelectedAccount,
  setPreselectedAccountFromGlobal,
  addUserAccount,
  removeUserAccount,
  markAccountAsNotNew,
  cleanupNewAccounts,
  updateUserAccount,
  setManagedUserLoading,
  setUserAccountsLoading,
  setSaveLoading,
  setSaveSuccess,
  setSaveError,
  resetUserAccountManagement,
  resetEditUserStatus,
  setCreateUserStep,
  setEmailCheckStatus,
  setCheckedEmail,
  setExistingUser,
  setEmailError,
  setPreFilledUserData,
  resetCreateUserFlow,
  resetCreateUserStatus,
  resetValidateUserStatus,
} = userAccountManagementSlice.actions;

export default userAccountManagementSlice.reducer;
